import type { TradeStudyCharts } from '../../../models/trade-study-charts.model';

interface TradeStudyChartsCsvOptions {
  currencyCode: string;
  selectedPartPersistentId: string | null;
}

type CsvCell = string | number;

type CsvRow = [string, string, string, string, number, string];

const HEADER: CsvCell[] = [
  'Chart',
  'Context',
  'Label',
  'Series',
  'Value',
  'Unit',
];

function escapeCsvCell(cell: CsvCell): string {
  // Security safeguard: prevents CSV/Excel formula injection by neutralizing
  // values that could be interpreted as formulas in spreadsheet applications.
  let value = String(cell);

  if (typeof cell === 'string') {
    value = sanitizeExcelCell(value);
  }

  if (!/[",\r\n]/.test(value)) {
    return value;
  }

  return `"${value.replaceAll('"', '""')}"`;
}

function sanitizeExcelCell(value: string): string {
  // Remove leading whitespace (Excel ignores it anyway visually, but attackers use it)
  const trimmed = value.replace(/^\s+/, '');

  // If it starts with a formula character, neutralize it
  if (/^[=+\-@]/.test(trimmed)) {
    return `'${value}`;
  }

  return value;
}

function rowToCsv(row: CsvCell[]): string {
  return row.map(escapeCsvCell).join(',');
}

function findPartName(
  charts: TradeStudyCharts,
  selectedPartPersistentId: string | null,
): string {
  if (!selectedPartPersistentId) {
    return 'Selected part';
  }

  return (
    charts.parts.find((part) => part.persistent_id === selectedPartPersistentId)
      ?.name ?? 'Selected part'
  );
}

export function buildTradeStudyChartsCsv(
  charts: TradeStudyCharts,
  options: TradeStudyChartsCsvOptions,
): string {
  const rows: CsvRow[] = [];
  const addRow = (
    chart: string,
    context: string,
    label: string,
    series: string,
    value: number,
    unit: string,
  ): void => {
    rows.push([chart, context, label, series, value, unit]);
  };

  for (const row of charts.capacity_vs_required_hours) {
    addRow(
      'Capacity vs Required Hours',
      'All shifts',
      row.shift_label,
      'Required',
      row.required_hours_per_month,
      'hr / mo',
    );
    addRow(
      'Capacity vs Required Hours',
      'All shifts',
      row.shift_label,
      'Capacity',
      row.capacity_hours_per_month,
      'hr / mo',
    );
  }

  for (const row of charts.completion_time_by_shift) {
    addRow(
      'Completion Time by Shift',
      'All shifts',
      row.shift_label,
      'Months to complete',
      row.months,
      'months',
    );

    addRow(
      'Completion Time by Shift',
      'All shifts',
      row.shift_label,
      'Days to complete',
      row.days,
      'days',
    );
  }

  for (const row of charts.cost_composition_by_process) {
    addRow(
      'Cost Composition by Part',
      'Root parts',
      row.process_name,
      'Material Cost',
      row.subtotal_material_cost,
      options.currencyCode,
    );
    addRow(
      'Cost Composition by Part',
      'Root parts',
      row.process_name,
      'Labour Cost',
      row.subtotal_labour_cost,
      options.currencyCode,
    );
    addRow(
      'Cost Composition by Part',
      'Root parts',
      row.process_name,
      'CapEx',
      row.subtotal_capex,
      options.currencyCode,
    );
  }

  for (const row of charts.time_per_part) {
    addRow(
      'Time per Part',
      'Root parts',
      row.process_name,
      'Total Time',
      row.total_time,
      'hours',
    );
  }

  for (const row of charts.cost_per_part) {
    addRow(
      'Cost per Part',
      'Root parts',
      row.process_name,
      'Total Cost',
      row.total_cost,
      options.currencyCode,
    );
  }

  const selectedPartName = findPartName(
    charts,
    options.selectedPartPersistentId,
  );
  const selectedProcessRows = options.selectedPartPersistentId
    ? (charts.processes_by_part[options.selectedPartPersistentId] ?? [])
    : [];
  const selectedCostCompositionRows = options.selectedPartPersistentId
    ? (charts.cost_composition_by_part[options.selectedPartPersistentId] ?? [])
    : [];

  for (const row of selectedProcessRows) {
    addRow(
      'Time per Process',
      selectedPartName,
      row.process_name,
      'Total Time',
      row.total_time,
      'hours',
    );
  }

  for (const row of selectedProcessRows) {
    addRow(
      'Cost per Process',
      selectedPartName,
      row.process_name,
      'Total Cost',
      row.total_cost,
      options.currencyCode,
    );
  }

  for (const row of selectedCostCompositionRows) {
    addRow(
      'Cost Composition by Process',
      selectedPartName,
      row.process_name,
      'Material Cost',
      row.subtotal_material_cost,
      options.currencyCode,
    );
    addRow(
      'Cost Composition by Process',
      selectedPartName,
      row.process_name,
      'Labour Cost',
      row.subtotal_labour_cost,
      options.currencyCode,
    );
    addRow(
      'Cost Composition by Process',
      selectedPartName,
      row.process_name,
      'CapEx',
      row.subtotal_capex,
      options.currencyCode,
    );
  }

  for (const row of charts.time_distribution) {
    addRow(
      'Time Distribution',
      'Root parts',
      row.process_name,
      'Total Time',
      row.total_time,
      'hours',
    );
  }

  return [HEADER, ...rows].map(rowToCsv).join('\n');
}
